/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return -- redux core: IAction/IPromiseAction used in ~38 reducers/sagas; the thunk/promise dispatch boundary ferries arbitrary action payloads (typed `any` by design) */
import { Middleware, Action } from '@reduxjs/toolkit'
import { Dispatch, MiddlewareAPI } from 'redux'
import { Selector } from 'reselect'
import { IApi } from '../api'
import { permissionDenied, sessionExpired } from './profile/actions'
import { IRootState } from './reducer'

export interface IAction<T = any> extends Action {
  /**
   * Which dispatch this action belongs to, stamped on the REQUEST and carried to
   * its SUCCESS/FAILURE — see the counter below.
   */
  requestId?: number
  /** When a SUCCESS or a websocket event arrived, so a reducer can compare times and stay pure. */
  receivedAt?: number
  promise?: (api: IApi) => Promise<T>
  payload?: any
  data?: T
  loadMore?: boolean
  params?: any
  // Populated by the middleware on FAILURE actions (see apiMiddleware below —
  // rejected promises dispatch `{ ...rest, error, type: FAILURE }`). Reducers
  // can read it directly without casting.
  error?: Error & { code?: string }
}

// Read `action.params` with type safety in a reducer. `IAction.params` is
// intentionally `any` (it varies per action), so member access on it trips
// `@typescript-eslint/no-unsafe-member-access`. Override the params shape with
// this helper rather than intersecting `IAction` directly — a plain
// `IAction & { params: P }` collapses back to `any` because `any & X` is `any`.
//
//   reducer(state, action: IActionWithParams<IPaginationParams>) {
//     if (!action.params?.loadMore) { ... }   // typed, lint-safe
//   }
export type IActionWithParams<P, T = any> = Omit<IAction<T>, 'params'> & {
  params?: P
}

// Shared params shape for the pagination/list-loading reducer pattern. Action
// creators pass `{ loadMore }` (append vs. replace) and `{ skipLoader }` (skip
// the loading spinner on background refreshes).
export interface IPaginationParams {
  loadMore?: boolean
  skipLoader?: boolean
}

export interface IPromiseAction {
  type: string[]
  promise: (api: IApi) => Promise<any>
  payload?: any
  [key: string]: any
}

type ThunkAction = (getState: (selector: Selector<IRootState, any>) => any) => any

/**
 * Stamps every promise action's REQUEST and its eventual SUCCESS/FAILURE with the
 * same id, so a reducer can tell which dispatch a reply belongs to.
 *
 * The middleware only labels; it decides nothing. Whether a superseded reply is
 * worth dropping depends on what the action means — a search that has been typed
 * past is stale, two saves fired together are both wanted — and only the reducer
 * receiving it knows which it is. Process-wide and monotonic: ids are compared
 * for equality by one reducer at a time, never across slices.
 */
let nextRequestId = 0

const apiMiddleware =
  (api: IApi): Middleware<object, IRootState> =>
  (storeApi: MiddlewareAPI<Dispatch<Action>, IRootState>) => {
    const { dispatch } = storeApi
    return (next) => (action: unknown) => {
      if (typeof action === 'function') {
        return dispatch(
          (action as ThunkAction)((selector: Selector<IRootState, any>) =>
            selector(storeApi.getState()),
          ),
        )
      }

      const { promise, type, ...rest } = action as IPromiseAction

      if (!promise) {
        return next(action)
      }

      const [REQUEST, SUCCESS, FAILURE] = type
      // const startTime = performance.now()

      const requestId = (nextRequestId += 1)
      next({ ...rest, requestId, type: REQUEST })

      return promise(api).then(
        (data: object) => {
          return next({ ...rest, data, requestId, receivedAt: Date.now(), type: SUCCESS })
        },
        (
          error: Error & {
            code: string
            tokenGeneration?: number
          },
        ) => {
          if (error && error.code === 'AUTHORIZATION_ERROR') {
            // Sign out locally only — never `logout()`, which would revoke whatever token is
            // in storage right now rather than the one this request failed with. See
            // `sessionExpired` for what that cost us.
            //
            // Only if the token that failed is still the token this tab holds.
            //
            // A 401 is evidence about the token the request carried and nothing else, and by
            // the time it lands the tab may be holding a different, healthy one — `setToken`
            // runs on every login path, so a reply that outlived a sign-in is enough. Acting on
            // the token in hand would sign the user out of the session they had just created,
            // which is the reported symptom in its most literal form.
            //
            // Generation rather than the token string: the comparison is the same, but nothing
            // has to put a JWT on an object that ends up in redux state.
            //
            // `token` is checked too, so an unauthenticated request 401ing does not stage a
            // sign-out from a session that was never there. And this doubles as the burst
            // guard — `removeToken` bumps the generation, so every straggler behind the first
            // is stale by construction rather than by timing.
            const failedOurCurrentToken =
              api.client.token !== null && error.tokenGeneration === api.client.tokenGeneration

            if (failedOurCurrentToken) {
              dispatch(sessionExpired())
            }
          }
          if (error && error.code === 'PERMISSION_DENIED') {
            dispatch(permissionDenied())
          }
          return next({ ...rest, error, requestId, type: FAILURE })
        },
      )
    }
  }

export default apiMiddleware
